/**
 * useHeartMonitor — Web Bluetooth hook that connects to a Heart Rate
 * Service (0x180D) device and delivers a live stream of { bpm, rmssd,
 * rhythmScore, sdnn, pnn50, rrIntervals } to the caller.
 *
 * Design choices:
 *   - Purely opt-in: hook does nothing until caller invokes connect().
 *     Browser shows native Bluetooth picker; user selects device; we
 *     subscribe to HR Measurement notifications.
 *   - Rolling 60-second RR window — matches the HRV-literature consensus
 *     for "live" RMSSD readouts. Calibrates in ~60s.
 *   - Disconnect handling: GATT server disconnect event → state goes to
 *     `disconnected`; caller decides how to surface. Straps commonly drop
 *     for a few seconds then auto-reconnect — we expose `reconnect()`.
 *   - All calculations run in lib/hrv.js (pure). This hook only owns the
 *     BLE transport and the time-windowed buffer.
 *   - Gracefully no-ops in browsers without Web Bluetooth (Safari/Firefox).
 *     Caller reads `isSupported` to decide whether to render the UI.
 *
 * Usage:
 *   const hm = useHeartMonitor({ windowMs: 60_000 });
 *   if (!hm.isSupported) return <SafariFallback />;
 *   hm.connect();            // opens picker
 *   hm.bpm, hm.rmssd, hm.rhythmScore  // live values
 *   hm.onRPeak = (tsMs) => { ... };   // sub to R-peaks (Phase 2)
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import {
  parseHeartRateMeasurement,
  computeRmssd,
  computeSdnn,
  computePnn50,
  computeRhythmScore,
  computeBpmFromRr,
  trimToWindow,
} from '../lib/hrv';

const HR_SERVICE = 'heart_rate';             // 0x180D — resolved name in Web Bluetooth
const HR_MEASUREMENT = 'heart_rate_measurement'; // 0x2A37

export const useHeartMonitor = ({ windowMs = 60_000 } = {}) => {
  // Feature-detect once. Undefined on iOS Safari + Firefox desktop.
  const isSupported = typeof navigator !== 'undefined'
    && typeof navigator.bluetooth !== 'undefined';

  // Connection lifecycle: idle → connecting → connected → disconnected|error
  const [status, setStatus] = useState('idle');
  const [deviceName, setDeviceName] = useState(null);
  const [error, setError] = useState(null);

  // Live readouts.
  const [bpm, setBpm] = useState(null);
  const [rmssd, setRmssd] = useState(null);
  const [rhythmScore, setRhythmScore] = useState(null);
  const [sampleCount, setSampleCount] = useState(0);

  // Refs — BLE objects don't belong in React state (they trigger re-renders
  // on every change and are non-serialisable). Buffers and R-peak handler
  // are also refs because callers want to subscribe without causing
  // re-renders of this hook's consumers.
  const deviceRef = useRef(null);
  const characteristicRef = useRef(null);
  const rrBufferRef = useRef([]);
  const rPeakListenerRef = useRef(null);
  const lastPeakTimestampRef = useRef(null);

  // R-peak subscribe API. Caller assigns a function; we invoke it with the
  // ctx.currentTime-equivalent timestamp (in ms) whenever a new R-peak
  // arrives. Used by the heartbeat scheduler to fire an audio beat on the
  // user's actual heartbeat (Phase 2 "sync heartbeat to my heart").
  const setOnRPeak = useCallback((fn) => {
    rPeakListenerRef.current = typeof fn === 'function' ? fn : null;
  }, []);

  const handleMeasurement = useCallback((event) => {
    const dv = event.target.value;
    const { bpm: bpmRaw, rrIntervals } = parseHeartRateMeasurement(dv);

    if (rrIntervals.length > 0) {
      rrBufferRef.current.push(...rrIntervals);
      trimToWindow(rrBufferRef.current, windowMs);

      // R-peak callbacks. A single BLE notification may contain multiple
      // RR intervals — Polar (and most chest straps) batch up to 4 RRs
      // when contact is briefly lost and recovers. Firing the listener
      // once per RR at the SAME timestamp would stack 2-4 audio beats on
      // top of each other (single loud beat) then leave silence until the
      // next batch — exactly the "erratic every ~10s" symptom users
      // report when their strap loses contact.
      //
      // Fix: only fire the callback for the MOST RECENT R-peak per BLE
      // notification. The older intervals in the batch are real and still
      // contribute to RMSSD / Rhythm / BPM calculations (rrBufferRef
      // already has them above) — but for live audio sync, only "now"
      // matters; replaying past missed beats produces overlap not
      // continuity.
      const now = performance.now();
      lastPeakTimestampRef.current = now;
      if (rPeakListenerRef.current) {
        const lastRr = rrIntervals[rrIntervals.length - 1];
        try { rPeakListenerRef.current(now, lastRr); }
        catch (e) {
          // R-peak listener threw — log so a bug in the audio scheduler
          // (its only caller) surfaces in DevTools instead of producing
          // silent audio gaps. Don't rethrow: one bad packet shouldn't
          // tear down the entire BLE session.
          console.warn('[useHeartMonitor] R-peak listener threw:', e);
        }
      }

      // Update derived metrics. cheap with a 60s window (~60 intervals).
      const buf = rrBufferRef.current;
      const nextBpm = computeBpmFromRr(buf) ?? bpmRaw ?? null;
      setBpm(nextBpm != null ? Math.round(nextBpm) : null);
      setRmssd(computeRmssd(buf));
      setRhythmScore(computeRhythmScore(buf));
      setSampleCount(buf.length);
    } else if (bpmRaw != null) {
      // Low-end strap without RR support — show BPM only, HRV unavailable.
      setBpm(bpmRaw);
    }
  }, [windowMs]);

  const cleanup = useCallback(() => {
    const ch = characteristicRef.current;
    if (ch) {
      try { ch.removeEventListener('characteristicvaluechanged', handleMeasurement); }
      catch (e) {
        // GATT object can be stale if the device dropped out before we
        // got a chance to unsubscribe. Safe to swallow at this layer —
        // the device disconnect below will clean up regardless.
        console.debug('[useHeartMonitor] characteristic unsubscribe failed (likely already detached):', e?.message);
      }
    }
    const dev = deviceRef.current;
    if (dev?.gatt?.connected) {
      try { dev.gatt.disconnect(); }
      catch (e) {
        console.debug('[useHeartMonitor] GATT disconnect failed (likely already disconnected):', e?.message);
      }
    }
    characteristicRef.current = null;
    deviceRef.current = null;
    rrBufferRef.current = [];
    setBpm(null);
    setRmssd(null);
    setRhythmScore(null);
    setSampleCount(0);
  }, [handleMeasurement]);

  const connect = useCallback(async () => {
    if (!isSupported) {
      setError('Web Bluetooth not supported in this browser');
      return;
    }
    setError(null);
    setStatus('connecting');

    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [HR_SERVICE] }],
        optionalServices: [HR_SERVICE],
      });
      deviceRef.current = device;
      setDeviceName(device.name || 'Heart monitor');

      device.addEventListener('gattserverdisconnected', () => {
        setStatus('disconnected');
      });

      const server = await device.gatt.connect();
      const service = await server.getPrimaryService(HR_SERVICE);
      const characteristic = await service.getCharacteristic(HR_MEASUREMENT);
      characteristic.addEventListener('characteristicvaluechanged', handleMeasurement);
      await characteristic.startNotifications();
      characteristicRef.current = characteristic;

      setStatus('connected');
    } catch (e) {
      // User-cancel in the picker shows up as a NotFoundError or
      // AbortError. Treat both as benign idle, not errors.
      const msg = e?.message || String(e);
      if (/user cancelled|NotFoundError|AbortError/i.test(msg)) {
        setStatus('idle');
      } else {
        setError(msg);
        setStatus('error');
      }
      cleanup();
    }
  }, [isSupported, handleMeasurement, cleanup]);

  const disconnect = useCallback(() => {
    cleanup();
    setStatus('idle');
    setDeviceName(null);
  }, [cleanup]);

  // Clean up on unmount so a dev-HMR reload doesn't leave a zombie GATT
  // connection hanging.
  useEffect(() => () => cleanup(), [cleanup]);

  return {
    isSupported,
    status,          // 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error'
    deviceName,
    error,
    bpm,
    rmssd,
    rhythmScore,
    sampleCount,     // number of RR intervals in buffer — drives "Calibrating..." UI
    connect,
    disconnect,
    setOnRPeak,
    lastPeakTimestampRef,
  };
};

export default useHeartMonitor;
